import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import classNames from 'classnames';

import { buttonUIComponent, formAddOnUIComponent } from 'ctm-css-themes';
import { applyVerticalStyles, shapedPropsWarning } from 'ctm-util-functions';

import { LoadingAnimation } from '../loading_animation/loading_animation';
import { withStyleProxy } from '../../higher_order_components/with_style_proxy';
import { withRules } from '../../higher_order_components/with_rules';
import { getContent } from '../utils';

import styles from './button.css';

const UIComponents = { ...buttonUIComponent, ...formAddOnUIComponent };

/**
 * Button React Component
 * @desc Button component that allows to create buttons using <button> or <a> html elements.
 *       Allows to use variations based on the css modules and the properties passed
 *       to the component as well as the variant value.
 * @returns {Node} a Button Component
 */
class _Button extends PureComponent {

    constructor(props) {
        super(props);
        // @todo remove once all shapedProps are gone
        shapedPropsWarning(props, 'Button');
        this.styles = applyVerticalStyles(styles, 'button');
    }

    /**
     * Handler for the onClick Event
     * @param  {Object} event - the onClick event object
     * @returns {Boolean} Always return true to allow events to bubble
     */
    onClick = (event) => {
        const { onClick, onClickFunction, onClickActions, dispatch } = this.props;

        // @warn onClick is currently used by ctm-datepicker
        // as a native method for clicking the Datepicker input.
        const clickMethod = onClickFunction || onClick;
        if (clickMethod) {
            clickMethod(event);
        } else if (onClickActions) {
            onClickActions.map((action) => dispatch(action));
        }
        return true;
    };

    /**
     * helper function to get either loading animation or real button content depending on if loading prop is true
     * @returns {Array} - children content for button
     */
    getButtonContent = () => {
        const { loading, loadingText } = this.props;
        return loading === true ? [loadingText, <LoadingAnimation key="loading" visualProps={{ variant: 'button' }} />] : getContent(this.props);
    };

    /**
     * Render the Button component
     * Don't render if its meant to be invisible!
     * @returns {Node} - Returns a <button/> or <a/>
     */
    render = () => {
        const { variant, UIComponent, className } = this.props;
        const { id, 'data-id': dataId, tagName, type, href, target, title } = this.props;
        const variantStyle = this.styles[variant] || null;
        const component = UIComponents[UIComponent] || null;
        const componentType = tagName || (href ? 'a' : 'button');
        const combinedClassName = classNames(className, component, variantStyle);
        const content = this.getButtonContent();

        /**
         * Note: Only standard DOM attributes should be used when creating a native DOM node.
         *       More info here in https://facebook.github.io/react/warnings/unknown-prop.html
         */
        const props = {
            id: id || dataId,
            'data-id': dataId,
            className: combinedClassName,
            type: componentType === 'button' ? type || 'button' : undefined,
            href,
            target,
            title,
            onClick: this.onClick
        };

        // Render the component.
        return React.createElement(componentType, props, content);
    };

}

_Button.propTypes = {
    id: PropTypes.string,
    'data-id': PropTypes.string,
    title: PropTypes.string,
    /**
     * Tag name of the element
     * 'a' and 'button' are specific for this component.
     */
    tagName: PropTypes.oneOf(['a', 'button']),

    /**
     * Type of the Button
     */
    type: PropTypes.oneOf(['button', 'reset', 'submit', 'menu']),

    /**
     * Href for the button anchor tag.
     */
    href: PropTypes.string,

    /**
     * Target blank for new window
     */
    target: PropTypes.string,

    /**
     * Property used to display an label.
     */
    // eslint-disable-next-line react/no-unused-prop-types
    label: PropTypes.string,
    /**
     * Property used to display an icon.
     */
    // eslint-disable-next-line react/no-unused-prop-types
    icon: PropTypes.string,

    /**
     * Property used to set the position of the icon relative to its label.
     */
    // eslint-disable-next-line react/no-unused-prop-types
    iconPosition: PropTypes.oneOfType([PropTypes.oneOf(['before', 'after'])]),

    /**
     * Property used to determine if we show the loading animation
     */
    loading: PropTypes.bool,

    /**
     * Property used to display the loading text
     */
    loadingText: PropTypes.string,

    /**
     * Property used as onClick handler.
     */
    onClick: PropTypes.oneOfType([
        /**
         * Action's name to dispatch.
         */
        PropTypes.string,

        /**
         * Callback function.
         */
        PropTypes.func
    ]),
    // @todo clear this or onClick
    onClickFunction: PropTypes.func,
    // doesn't change once component is mounted
    onClickActions: PropTypes.array,
    // This is supplied by redux and will always be present on a connected component
    dispatch: PropTypes.func,
    className: PropTypes.string,
    UIComponent: PropTypes.string,
    variant: PropTypes.string
};

// Apply the visual Props Proxy Higher Order Component.
// connect is required so we can get dispatch on the button props
const Button = withRules(withStyleProxy(_Button));

// Export the Enhanced component.
export { Button, _Button };
